import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate } from "@/lib/format";
import { Search, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function GlobalSearch({ linkBase = "/buyer/pos", placeholder = "Search PO, enquiry number, supplier, item, material…" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const onChange = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setResults(null); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(v.trim())}`);
        setResults(r.data.results);
        setOpen(true);
      } catch (e) { /* noop */ }
    }, 250);
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        data-testid="global-po-search"
        value={q}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => results?.length && setOpen(true)}
        placeholder={placeholder}
        className="pl-9 bg-slate-50"
      />
      {open && results && (
        <div data-testid="global-search-results" className="absolute left-0 right-0 top-11 z-50 max-h-96 overflow-y-auto rounded-lg border bg-white shadow-lg">
          {results.length === 0 && <p className="p-4 text-sm text-muted-foreground">No results for "{q}".</p>}
          {results.map((r) => (
            <button
              key={`${r.kind || "po"}-${r.id}`}
              data-testid={`search-result-${r.po_number}`}
              onClick={() => { setOpen(false); setQ(""); navigate(r.kind === "enquiry" ? `/buyer/enquiries/${r.id}` : `${linkBase}/${r.id}`); }}
              className="flex w-full items-center gap-3 border-b px-4 py-3 text-left transition-colors last:border-0 hover:bg-slate-50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold">{r.po_number}</span>
                  {r.kind === "enquiry"
                    ? <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-700">Enquiry · {r.action_required}</span>
                    : <StatusBadge status={r.status} />}
                </div>
                <p className="truncate text-xs text-muted-foreground">{r.kind === "enquiry" ? r.item : `${r.supplier_name} · ${r.item}`}</p>
                {r.kind !== "enquiry" && <p className="text-xs text-muted-foreground">Expected: {fmtDate(r.expected_delivery_date)} · <span className="font-medium text-slate-700">{r.action_required}</span></p>}
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
